import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Grid, Paper } from '@material-ui/core';

import { GlobalContext } from '../../../store';
import { TStandingType } from '../../../store/admin/eventAwards/store';
import { ISeason } from '../../../store/seasons/store';

import SeasonSelector from '../../../components/AutoComplete';
import { useBoolean } from '../../common/Hooks/useBoolean.hook';
import { useSelectedStanding } from '../../common/Hooks/useSelectedStanding';
import { getSeasonStandingData } from '../../common/Utils/getSeasonStandingData';
import { Subheader } from '../Common/Subheader/Subheader';
import { StandingsSelector } from './Components/StandingsSelector';
import { StandingsTable } from './Components/StandingsTable';
import { StandingsMetadata } from './Components/StandingsMetadata';
import { useClubs } from './Hooks/useClubs.hook';
import { useSeasons } from './Hooks/useSeasons.hook';
import { useMetaSeasonAwards } from './useMetaSeasonAwards';
import { blankSeasonStandingsMetadata } from './Utils/blankSeasonStandingsMetadata';

export const getSeasonsOptions = (seasons: ISeason[]) =>
  seasons.map((season: ISeason) => ({
    label: season.year,
    value: String(season.id)
  }));

export type TStandingDescription = 'cashDescription' | 'pointsDescription' | 'scripDescription';

export type TStandingName = 'cashName' | 'pointsName' | 'scripName';

export const standingTypes: TStandingType[] = ['points', 'cash', 'scrip'];

interface IProps {
  clubId: number;
}

const Standings = (props: IProps) => {
  const { clubId } = props;

  const {
    actions: {
      adminClubs: { get: getClubs },
      adminEvents: { all: getAllEvents },
      seasons: { update: updateSeason },
      adminSeasonAwards: { get: getSeasonAwards }
    },
    adminClubs: clubs,
    adminSeasonAwards: seasonAwards,
    seasons
  } = useContext(GlobalContext);

  const [club] = useClubs(clubId, clubs, getClubs);

  useEffect(() => {
    getAllEvents(clubId).catch();
  }, [clubId]);

  const { currentSeason, seasonsOptions, onSeasonSelect } = useSeasons(
    seasons,
    clubId,
    getSeasonAwards
  );

  const [selectedStanding, onSetSelectedStanding] = useSelectedStanding();

  const [editMode, setEditMode, onToggleEditMode] = useBoolean(false);

  const [editSeason, setEditSeason] = useState(blankSeasonStandingsMetadata);

  useEffect(() => {
    if (currentSeason) {
      const {
        cashDescription,
        cashName,
        pointsDescription,
        pointsName,
        scripDescription,
        scripName
      } = currentSeason;

      setEditSeason({
        cashDescription,
        cashName,
        pointsDescription,
        pointsName,
        scripDescription,
        scripName
      });
    }
  }, [editMode, currentSeason]);

  useEffect(() => {
    setEditMode(false);
  }, [selectedStanding]);

  const onSetEditSeason = useCallback(
    (field: string) => (event: any) => {
      setEditSeason({ ...editSeason, [`${selectedStanding}${field}`]: event.target.value });
    },
    [editSeason]
  );

  const onSaveSeason = useCallback(async () => {
    await updateSeason(clubId, currentSeason.id, editSeason);
    setEditMode(false);
  }, [editSeason]);

  const { name: standingName, description: standingDescription } = getSeasonStandingData(
    editSeason,
    selectedStanding
  );

  const [currentSeasonMetaAwards] = useMetaSeasonAwards({
    currentSeason,
    seasonAwards,
    selectedStanding
  });

  return (
    <div className="admin-layout">
      {!!club && <Subheader clubId={club.id} profileImage={club.profileImage} title="STANDINGS" />}
      <div className="admin-wrapper">
        <div className="standings">
          {currentSeason && (
            <Grid className="grid" container={true} spacing={8}>
              <Grid className="grid-item standings-selectors" item={true} xs={12} md={4}>
                <SeasonSelector
                  value={{ value: String(currentSeason.id), label: currentSeason.year }}
                  outlined={true}
                  title={'SEASON'}
                  onChange={onSeasonSelect}
                  options={seasonsOptions}
                />

                <StandingsSelector
                  onSetSelectedStanding={onSetSelectedStanding}
                  selectedStanding={selectedStanding}
                  currentSeason={currentSeason}
                />
              </Grid>

              <Grid className="standings-body" item={true} xs={12} md={8}>
                <Paper elevation={1} className="paper">
                  <StandingsMetadata
                    editMode={editMode}
                    standingName={standingName}
                    onSetEditSeason={onSetEditSeason}
                    onToggleEditMode={onToggleEditMode}
                    standingDescription={standingDescription}
                    onSaveSeason={onSaveSeason}
                  />

                  <StandingsTable
                    seasonMetaAwards={currentSeasonMetaAwards}
                    selectedStanding={selectedStanding}
                    clubId={clubId}
                  />
                </Paper>
              </Grid>
            </Grid>
          )}
        </div>
      </div>
    </div>
  );
};

export default Standings;
